import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Briefcase, ChevronDown, ChevronRight, User, ExternalLink } from "lucide-react";

const ROLE_BADGE = {
  senior_management: { label: "Senior Mgmt", color: "bg-red-100 text-red-800 border-red-200", Icon: Crown },
  hod: { label: "HOD", color: "bg-violet-100 text-violet-800 border-violet-200", Icon: Briefcase },
  manager: { label: "Manager", color: "bg-amber-100 text-amber-800 border-amber-200", Icon: Briefcase },
  hr: { label: "HR", color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: User },
  super_admin: { label: "Super Admin", color: "bg-slate-200 text-slate-800 border-slate-300", Icon: User },
  employee: { label: "Employee", color: "bg-slate-100 text-slate-700 border-slate-200", Icon: User },
};

export default function OrgChart() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org-chart");
        setRows(data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group children by "primary parent" (manager > hod > first senior_mgmt > none)
  const { tops, byParent, hasChildren } = useMemo(() => {
    const m = {};
    const top = [];
    const has = {};
    const idSet = new Set(rows.map((r) => r.id));
    for (const r of rows) {
      const parent =
        (r.reports_to_manager && idSet.has(r.reports_to_manager) && r.reports_to_manager) ||
        ((r.reports_to_hod || r.reports_to) && idSet.has(r.reports_to_hod || r.reports_to) && (r.reports_to_hod || r.reports_to)) ||
        (Array.isArray(r.reports_to_senior_mgmt_ids) &&
          r.reports_to_senior_mgmt_ids.find((x) => idSet.has(x))) ||
        (r.reports_to_senior_mgmt && idSet.has(r.reports_to_senior_mgmt) && r.reports_to_senior_mgmt) ||
        null;
      if (parent) {
        m[parent] = m[parent] || [];
        m[parent].push(r);
        has[parent] = true;
      } else {
        top.push(r);
      }
    }
    return { tops: top, byParent: m, hasChildren: has };
  }, [rows]);

  // Flatten the tree with depth info using iterative DFS
  const flat = useMemo(() => {
    const out = [];
    const stack = [...[...tops].reverse().map((e) => ({ emp: e, depth: 0 }))];
    while (stack.length) {
      const cur = stack.pop();
      out.push(cur);
      const kids = byParent[cur.emp.id] || [];
      if (!collapsed.has(cur.emp.id)) {
        for (let i = kids.length - 1; i >= 0; i--) {
          stack.push({ emp: kids[i], depth: cur.depth + 1 });
        }
      }
    }
    return out;
  }, [tops, byParent, collapsed]);

  const toggle = (id) =>
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="space-y-5" data-testid="org-chart">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Hierarchy</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Org Chart
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {loading ? "Loading..." : `${rows.length} portal-enabled employees · click a row to expand / collapse subordinates`}
        </p>
      </div>
      {!loading && rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-red-100 p-8 text-center">
          <Crown className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <div className="text-sm text-slate-600 font-medium">No portal-enabled employees yet</div>
        </div>
      ) : (
        <div className="space-y-2">
          {flat.map(({ emp, depth }) => {
            const meta = ROLE_BADGE[emp.role] || ROLE_BADGE.employee;
            const Icon = meta.Icon;
            const isExpandable = !!hasChildren[emp.id];
            const isExpanded = !collapsed.has(emp.id);
            return (
              <div
                key={emp.id}
                style={{ marginLeft: depth * 28 }}
                className="flex items-center gap-3 bg-white border border-red-100 rounded-xl p-3 hover:border-red-300 transition-colors"
                data-testid={`org-node-${emp.id}`}
              >
                {isExpandable ? (
                  <button
                    onClick={() => toggle(emp.id)}
                    className="text-slate-400 hover:text-red-700"
                    data-testid={`org-toggle-${emp.id}`}
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                ) : (
                  <span className="w-4" />
                )}
                <div className="h-10 w-10 rounded-md bg-gradient-to-br from-red-50 to-white border border-red-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {emp.profile_photo_url ? (
                    <img src={`/api/files/${emp.profile_photo_url}`} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Icon className="h-5 w-5 brand-text" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 text-sm truncate">{emp.name}</span>
                    <span className={`text-[10px] font-medium border px-1.5 py-0.5 rounded ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {emp.designation || "—"} · {emp.department_name || "—"} {emp.punch_id ? `· ${emp.punch_id}` : ""}
                  </div>
                </div>
                <Link
                  to={`/employees/${emp.id}`}
                  className="opacity-60 hover:opacity-100 hover:text-red-700"
                  title="Open employee"
                  data-testid={`org-open-${emp.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
